import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { customerApiService } from 'services/api';
import { Employee } from 'types';

interface EmployeeData {
  employees: Employee[];
  loading: boolean;
  error: string | null;
}

export const useEmployees = () => {
  const { user, company } = useAuth();
  const [employeeData, setEmployeeData] = useState<EmployeeData>({
    employees: [],
    loading: true,
    error: null
  });

  const fetchEmployees = async () => {
    if (!user || !company) {
      setEmployeeData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setEmployeeData(prev => ({ ...prev, loading: true, error: null }));

      const response = await customerApiService.getEmployees();

      if (response.success && response.data) {
        const employees = response.data.map((emp: any) => ({
          id: emp.id,
          name: `${emp.firstName} ${emp.lastName}`,
          employeeId: emp.employeeId,
          email: emp.email,
          firstName: emp.firstName,
          lastName: emp.lastName,
          phone: emp.phone,
          position: emp.position,
          department: emp.position || 'Unknown',
          grade: 'Associate', // Default grade
          status: (emp.isActive ? 'active' : 'inactive') as 'active' | 'inactive' | 'suspended' | 'pending',
          walletBalance: emp.walletBalance || 0,
          creditLimit: emp.creditLimit || 0,
          creditUsed: emp.creditUsed || 0,
          kycStatus: emp.kycStatus || 'PENDING',
          joinDate: emp.createdAt,
          createdAt: emp.createdAt,
          updatedAt: emp.updatedAt,
          isActive: emp.isActive,
          profileImage: emp.profileImage,
          dateOfBirth: emp.dateOfBirth,
          company: emp.company
        }));

        setEmployeeData({
          employees,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.message || 'Failed to fetch employees');
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployeeData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch employees'
      }));
    }
  };

  const createEmployee = async (employeeData: Partial<Employee>) => {
    try {
      const response = await customerApiService.createEmployee({
        firstName: employeeData.name?.split(' ')[0] || '',
        lastName: employeeData.name?.split(' ').slice(1).join(' ') || '',
        email: `${employeeData.employeeId?.toLowerCase()}@${company?.domain || 'company.com'}`,
        employeeId: employeeData.employeeId || '',
        position: employeeData.department || 'Employee',
        isActive: employeeData.status === 'active'
      });

      if (response.success) {
        await fetchEmployees(); // Refresh the list
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to create employee');
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  };

  const updateEmployee = async (employeeId: string, updates: Partial<Employee>) => {
    try {
      const response = await customerApiService.updateEmployee(employeeId, {
        firstName: updates.name?.split(' ')[0] || '',
        lastName: updates.name?.split(' ').slice(1).join(' ') || '',
        position: updates.department || 'Employee',
        isActive: updates.status === 'active'
      });

      if (response.success) {
        await fetchEmployees(); // Refresh the list
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update employee');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const deleteEmployee = async (employeeId: string) => {
    try {
      const response = await customerApiService.deleteEmployee(employeeId);

      if (response.success) {
        await fetchEmployees(); // Refresh the list
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to delete employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  };

  const refreshData = () => {
    fetchEmployees();
  };

  useEffect(() => {
    fetchEmployees();
  }, [user, company]);

  return {
    ...employeeData,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    refreshData
  };
};
